import { useEffect, useState, Fragment } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/Toast';
import { useModal } from '../components/ModalProvider';
import * as kpiApi from '../api/kpi';
import { getYearTargets, saveYearTargets } from '../api/revenue';
import { fmtDate, fmtVND, fmtNum, roleName, taskCompletion, itemStats } from '../utils/format';
import { KpiPct, StatusChip } from '../components/Chips';
import { RatioBadge } from '../components/Chips';
import AttendanceTab from './AttendanceTab';
import PayslipPage from './PayslipPage';
import PayrollAdminPage from './PayrollAdminPage';
import { attPendingCount } from '../api/attendance';

export default function KpiPage() {
  const { me } = useAuth();
  const toast = useToast();
  const { openModal, closeModal } = useModal();
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState('kpi'); // 'kpi' | 'attendance' | 'salary' | 'oppay'
  // Tab "Lương phòng OP": chỉ GĐ/CV + Kế toán trưởng (trưởng phòng "Phòng kế toán").
  const canOpPay = me.role === 'GD' || me.role === 'CV' || (me.deptHeadOf || []).includes('Phòng kế toán');
  const [attPending, setAttPending] = useState(0); // số đơn chấm công chờ mình duyệt
  const [loading, setLoading] = useState(true);
  const [snapping, setSnapping] = useState(false);
  const [months, setMonths] = useState([]); // các tháng đã chốt
  const [month, setMonth] = useState(''); // '' = tháng này (trực tiếp)
  const [sortCol, setSortCol] = useState('actual'); // mặc định: doanh thu thực tế
  const [sortDir, setSortDir] = useState('desc'); // cao -> thấp
  const [collapsed, setCollapsed] = useState({}); // {tênPhòng: true} = đang thu gọn (bố cục 1b)
  const toggleGroup = (name) => setCollapsed((c) => ({ ...c, [name]: !c[name] }));

  // Giá trị dùng để so sánh cho từng cột.
  function sortValue(k, col) {
    switch (col) {
      case 'name': return k.name || '';
      case 'role': return k.role || '';
      case 'totalTasks': return k.totalTasks || 0;
      case 'onTimeRate': return k.onTimeRate || 0;
      case 'lateCount': return k.lateCount || 0;
      case 'target': return k.rev ? k.rev.target : -1;
      case 'actual': return k.rev ? k.rev.actual : -1;
      case 'expected': return k.expectedTotal || 0;
      case 'pct': return k.rev ? k.rev.pct : -1;
      case 'debtRecover': return k.debtRecover || 0;
      case 'baseSalary': return k.baseSalary || 0;
      case 'allowance': return k.allowance || 0;
      default: return 0;
    }
  }

  const sortedRows = [...rows].sort((a, b) => {
    const av = sortValue(a, sortCol);
    const bv = sortValue(b, sortCol);
    let cmp;
    if (typeof av === 'string') cmp = av.localeCompare(bv, 'vi');
    else cmp = av - bv;
    return sortDir === 'asc' ? cmp : -cmp;
  });

  // Gom các dòng theo phòng ban (giữ nguyên thứ tự sắp xếp bên trong mỗi nhóm).
  const groupedRows = [];
  const groupIndex = {};
  sortedRows.forEach((k) => {
    const g = k.groupName || 'Ban giám đốc / Chưa có phòng';
    if (groupIndex[g] == null) {
      groupIndex[g] = groupedRows.length;
      groupedRows.push({ name: g, rows: [] });
    }
    groupedRows[groupIndex[g]].rows.push(k);
  });

  function sortBy(col) {
    if (sortCol === col) setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    else {
      setSortCol(col);
      setSortDir('desc'); // lần đầu bấm 1 cột: cao -> thấp
    }
  }
  function sortArrow(col) {
    if (sortCol !== col) return <span style={{ color: '#94a3b8', opacity: 0.6 }}> ↕</span>;
    return sortDir === 'asc' ? (
      <span style={{ color: 'var(--ok)' }}> ▲</span>
    ) : (
      <span style={{ color: 'var(--accent)' }}> ▼</span>
    );
  }

  async function doSnapshot() {
    if (snapping) return;
    setSnapping(true);
    try {
      const res = await kpiApi.kpiSnapshot();
      toast(`${res.message} (${res.saved} nhân viên)`);
      kpiApi.kpiMonths().then(setMonths).catch(() => {});
    } catch (err) {
      toast(err.response?.data?.message || 'Có lỗi xảy ra', 'danger');
    } finally {
      setSnapping(false);
    }
  }

  function reload() {
    setLoading(true);
    return kpiApi
      .kpiBoard(month || undefined)
      .then(setRows)
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month]);

  useEffect(() => {
    kpiApi.kpiMonths().then(setMonths).catch(() => {});
  }, []);

  // Đơn chấm công chờ mình duyệt -> chấm đỏ ở tab. Làm mới khi rời tab Chấm công.
  useEffect(() => {
    attPendingCount().then((d) => setAttPending(d.count || 0)).catch(() => {});
  }, [tab]);

  const isPast = !!month; // đang xem tháng đã chốt (chỉ đọc)

  function openDetail(userId) {
    if (isPast) return; // tháng đã chốt: chỉ xem bảng, không mở chi tiết trực tiếp
    openModal(<KpiDetailModal userId={userId} onClose={closeModal} onOpenExpected={(uid) => openExpected(uid)} />);
  }

  function openExpected(userId) {
    openModal(
      <ExpectedModal userId={userId} onBack={() => openDetail(userId)} onClose={closeModal} />
    );
  }

  function openRevenueModal() {
    openModal(<YearTargetGrid onClose={closeModal} onSaved={reload} toast={toast} />, { wide: true });
  }

  const overview = me.role === 'NV' && rows.length ? rows.find((r) => String(r.userId) === String(me.id)) : null;

  return (
    <>
      <div className="page-head">
        <div>
          <h2>KPI & đánh giá</h2>
          <div className="sub">{me.role === 'NV' ? 'Kết quả cá nhân' : me.role === 'TP' ? 'Nhóm của bạn' : 'Toàn công ty'}</div>
        </div>
        {tab === 'kpi' && (
        <div className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <select value={month} onChange={(e) => setMonth(e.target.value)} style={{ width: 'auto' }} title="Chọn tháng KPI">
            {/* Tháng hiện tại = số liệu sống ('' = trực tiếp), nhãn theo đúng tháng. */}
            <option value="">
              Tháng {String(new Date().getMonth() + 1).padStart(2, '0')}/{new Date().getFullYear()}
            </option>
            {/* Các tháng đã chốt — bỏ tháng hiện tại để không trùng. */}
            {months
              .filter((m) => m !== `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`)
              .map((m) => (
                <option key={m} value={m}>
                  Tháng {m.slice(5, 7)}/{m.slice(0, 4)}
                </option>
              ))}
          </select>
          {!isPast && me.role === 'GD' && (
            <button className="btn ghost" onClick={doSnapshot} disabled={snapping}>
              {snapping ? 'Đang chốt…' : '📅 Chốt KPI tháng này'}
            </button>
          )}
          {!isPast && me.role !== 'NV' && (
            <button className="btn ghost" onClick={openRevenueModal}>
              Nhập doanh thu khoán
            </button>
          )}
          {isPast && <span className="chip done">📌 KPI đã chốt — chỉ xem</span>}
        </div>
        )}
      </div>

      <div className="seg" style={{ marginBottom: 12 }}>
        <button className={tab === 'kpi' ? 'active' : ''} onClick={() => setTab('kpi')}>KPI trong tháng</button>
        <button className={tab === 'attendance' ? 'active' : ''} onClick={() => setTab('attendance')} style={{ position: 'relative' }}>
          Chấm công
          {attPending > 0 && <span title={`${attPending} đơn chờ duyệt`} style={{ position: 'absolute', top: 3, right: 4, width: 9, height: 9, borderRadius: '50%', background: 'var(--danger)', border: '1.5px solid var(--panel)' }} />}
        </button>
        <button className={tab === 'salary' ? 'active' : ''} onClick={() => setTab('salary')}>Bảng lương</button>
        {canOpPay && <button className={tab === 'oppay' ? 'active' : ''} onClick={() => setTab('oppay')}>Lương phòng OP</button>}
      </div>

      {tab === 'attendance' && <AttendanceTab me={me} />}
      {tab === 'salary' && <PayslipPage embedded />}
      {canOpPay && tab === 'oppay' && <PayrollAdminPage embedded fixedDepartment="Phòng OP" />}

      {tab === 'kpi' && (<>
      <details className="helpbox">
        <summary>Cách tính KPI</summary>
        <p>
          Tỷ lệ hoàn thành (Nhân viên): số KH đạt (NV note đúng hạn VÀ trưởng phòng duyệt "đạt") chia tổng KH được
          giao. Tỷ lệ hoàn thành (Trưởng phòng): số KH đã DUYỆT (đạt hoặc không đạt) TRƯỚC hạn duyệt chia tổng KH các
          việc của nhóm — miễn duyệt kịp hạn là tính KPI. Doanh thu: thực tế / khoán đầu tháng. Tỷ lệ cọc (theo
          tháng): tổng tiền đã thanh toán / tổng tiền của tất cả KH của nhân viên trong tháng (tự động từ SocSon, 1
          tuần/lần). Ở cột Doanh thu thực tế, số <span style={{ color: '#16a34a', fontWeight: 600 }}>(xanh)</span> là phần
          doanh thu hỗ trợ (hỗ trợ nhận từ nhóm khác + phần đi hỗ trợ nhóm khác), đã nằm trong doanh thu cá nhân.
        </p>
      </details>

      {overview && (
        <div className="grid kpi-grid" style={{ marginBottom: 16 }}>
          <div className="stat">
            <div className="label">Việc được giao</div>
            <div className="val">{overview.totalTasks}</div>
          </div>
          <div className="stat">
            <div className="label">Tỷ lệ hoàn thành</div>
            <div
              className="val"
              style={{ color: overview.onTimeRate >= 80 ? 'var(--ok)' : overview.onTimeRate >= 50 ? 'var(--warn)' : 'var(--danger)' }}
            >
              {overview.onTimeRate}%
            </div>
          </div>
          <div className="stat">
            <div className="label">KH trễ/không đạt</div>
            <div className="val" style={{ color: overview.lateCount ? 'var(--danger)' : 'var(--text)' }}>
              {overview.lateCount}
            </div>
          </div>
          <div className="stat">
            <div className="label">Doanh thu khoán</div>
            <div className="val sm">{overview.rev ? overview.rev.pct + '%' : '—'}</div>
          </div>
        </div>
      )}

      <div className="card">
        <h3>📊 Bảng KPI</h3>
        <div style={{ overflowX: 'auto' }}>
        <table>
          <thead>
            <tr>
              <th onClick={() => sortBy('name')} style={{ cursor: 'pointer', userSelect: 'none' }}>Nhân sự{sortArrow('name')}</th>
              <th onClick={() => sortBy('role')} style={{ cursor: 'pointer', userSelect: 'none' }}>Cấp{sortArrow('role')}</th>
              <th className="c" onClick={() => sortBy('totalTasks')} style={{ cursor: 'pointer', userSelect: 'none' }}>Tổng việc{sortArrow('totalTasks')}</th>
              <th className="c" onClick={() => sortBy('onTimeRate')} style={{ cursor: 'pointer', userSelect: 'none' }}>Tỷ lệ hoàn thành{sortArrow('onTimeRate')}</th>
              <th className="c" onClick={() => sortBy('lateCount')} style={{ cursor: 'pointer', userSelect: 'none' }}>KH không đạt{sortArrow('lateCount')}</th>
              <th className="r" onClick={() => sortBy('target')} style={{ cursor: 'pointer', userSelect: 'none' }}>DT khoán{sortArrow('target')}</th>
              <th className="r" onClick={() => sortBy('actual')} style={{ cursor: 'pointer', userSelect: 'none' }}>DT thực tế{sortArrow('actual')}</th>
              <th className="r" onClick={() => sortBy('expected')} style={{ cursor: 'pointer', userSelect: 'none' }}>DT dự kiến{sortArrow('expected')}</th>
              <th className="c" onClick={() => sortBy('pct')} style={{ cursor: 'pointer', userSelect: 'none' }}>% Doanh thu{sortArrow('pct')}</th>
              <th className="c" onClick={() => sortBy('debtRecover')} style={{ cursor: 'pointer', userSelect: 'none' }}>Tỷ lệ cọc{sortArrow('debtRecover')}</th>
              <th className="r" onClick={() => sortBy('baseSalary')} style={{ cursor: 'pointer', userSelect: 'none' }}>Lương CB{sortArrow('baseSalary')}</th>
              <th className="r" onClick={() => sortBy('allowance')} style={{ cursor: 'pointer', userSelect: 'none' }}>Phụ cấp{sortArrow('allowance')}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={12} className="empty">
                  Đang tải...
                </td>
              </tr>
            ) : sortedRows.length ? (
              groupedRows.map((grp) => (
                <Fragment key={grp.name}>
                  <tr className="kpi-group" style={{ cursor: 'pointer' }} onClick={() => toggleGroup(grp.name)}>
                    <td colSpan={12}>
                      <span style={{ display: 'inline-block', width: 14, transition: 'transform .15s', transform: collapsed[grp.name] ? 'rotate(-90deg)' : 'none' }}>▾</span>{' '}
                      {grp.name} <span className="small muted">({grp.rows.length})</span>
                    </td>
                  </tr>
                  {!collapsed[grp.name] && grp.rows.map((k) => (
                <tr key={k.userId}>
                  <td>
                    <a style={{ color: 'var(--accent)', cursor: 'pointer', fontWeight: 600 }} onClick={() => openDetail(k.userId)}>
                      {k.name}
                    </a>
                  </td>
                  <td>{roleName(k.role)}</td>
                  <td className="c">{k.totalTasks}</td>
                  <td className="c">
                    <KpiPct p={k.onTimeRate} />
                  </td>
                  <td className="c">{k.lateCount > 0 ? <span style={{ color: 'var(--danger)' }}>{k.lateCount}</span> : '0'}</td>
                  <td className="r">
                    {k.rev ? fmtVND(k.rev.target) : '—'}
                    {k.rev?.group && <span className="small muted"> (gộp nhóm)</span>}
                  </td>
                  <td className="r">
                    {k.rev ? fmtVND(k.rev.actual) : '—'}
                    {(() => {
                      const sup = (Number(k.rev?.supportCredit) || 0) + (Number(k.rev?.supportHandled) || 0);
                      return sup > 0 ? <span style={{ color: '#16a34a', fontWeight: 600 }}> ({fmtVND(sup)})</span> : null;
                    })()}
                  </td>
                  <td className="r">
                    <a
                      style={{ color: 'var(--accent)', cursor: 'pointer', fontWeight: 600 }}
                      onClick={() => openExpected(k.userId)}
                      title="Bấm để xem chi tiết theo trạng thái"
                    >
                      {fmtVND(k.expectedTotal || 0)} ▸
                    </a>
                  </td>
                  <td className="c">{k.rev ? <KpiPct p={k.rev.pct} /> : '—'}</td>
                  <td className="c">
                    <KpiPct p={k.debtRecover} />
                  </td>
                  <td className="r">{k.incomeHidden ? <span className="muted" title="NV thuộc nhiều phòng ban">🔒</span> : k.baseSalary ? fmtVND(k.baseSalary) : '—'}</td>
                  <td className="r">{k.incomeHidden ? <span className="muted">🔒</span> : k.allowance ? fmtVND(k.allowance) : '—'}</td>
                </tr>
                  ))}
                </Fragment>
              ))
            ) : (
              <tr>
                <td colSpan={12} className="empty">
                  Chưa có dữ liệu
                </td>
              </tr>
            )}
          </tbody>
        </table>
        </div>
      </div>
      </>)}
    </>
  );
}

function KpiDetailModal({ userId, onClose, onOpenExpected }) {
  const [data, setData] = useState(null);
  const [exp, setExp] = useState(null); // { total, expPct }
  const [history, setHistory] = useState([]);

  useEffect(() => {
    kpiApi.kpiDetail(userId).then(setData);
    kpiApi.kpiExpected(userId, 'all').then(setExp).catch(() => setExp({ total: 0, expPct: 0 }));
    kpiApi.kpiHistory(userId).then(setHistory).catch(() => setHistory([]));
  }, [userId]);

  if (!data) return null;
  const { user: u, kpi: k, tasks, customers } = data;

  return (
    <>
      <span className="close" onClick={onClose}>
        ×
      </span>
      <h3>
        {u.name} <span className={`role-badge ${u.role === 'TP' ? 'role-tp' : 'role-nv'}`}>{roleName(u.role)}</span>
      </h3>
      <div className="small muted" style={{ marginBottom: 14 }}>
        {u.group ? '' : 'Ban giám đốc'} · Đánh giá chi tiết trong tháng
      </div>

      <div className="grid kpi-grid" style={{ marginBottom: 16 }}>
        <div className="stat">
          <div className="label">Tổng việc</div>
          <div className="val">{k.totalTasks}</div>
        </div>
        <div className="stat">
          <div className="label">Tỷ lệ hoàn thành</div>
          <div className="val" style={{ color: k.onTimeRate >= 80 ? 'var(--ok)' : k.onTimeRate >= 50 ? 'var(--warn)' : 'var(--danger)' }}>
            {k.onTimeRate}%
          </div>
        </div>
        <div className="stat">
          <div className="label">KH không đạt</div>
          <div className="val" style={{ color: k.lateCount ? 'var(--danger)' : 'var(--text)' }}>
            {k.lateCount}
          </div>
        </div>
        <div className="stat">
          <div className="label">% Doanh thu</div>
          <div className="val sm">{k.rev ? k.rev.pct + '%' : '—'}</div>
        </div>
      </div>

      <div style={{ fontSize: 13, fontWeight: 700, margin: '6px 0 8px' }}>📋 Công việc trong tháng ({tasks.length})</div>
      {tasks.length ? (
        tasks.map((t) => {
          const comp = taskCompletion(t);
          const st = itemStats(t);
          const barColor = comp.pct >= 80 ? 'var(--ok)' : comp.pct >= 50 ? 'var(--warn)' : 'var(--danger)';
          return (
            <div className="card" key={t._id} style={{ background: 'var(--panel2)', marginBottom: 10 }}>
              <div className="row between" style={{ alignItems: 'flex-start' }}>
                <b>{t.title}</b>
                <StatusChip task={t} />
              </div>
              <div className="row between small" style={{ margin: '8px 0 4px' }}>
                <span className="muted">
                  {comp.done}/{comp.total} KH đạt
                </span>
                <span style={{ color: barColor, fontWeight: 700 }}>{comp.pct}%</span>
              </div>
              <div className="progress" style={{ marginBottom: 8 }}>
                <div style={{ width: `${comp.pct}%`, background: barColor }}></div>
              </div>
              <div className="row small" style={{ gap: 6, flexWrap: 'wrap' }}>
                {st.pending > 0 && <span className="chip todo">{st.pending} chưa làm</span>}
                {st.noted > 0 && <span className="chip review">{st.noted} chờ duyệt</span>}
                {st.approved > 0 && <span className="chip done">{st.approved} đạt</span>}
                {st.rejected > 0 && <span className="chip late">{st.rejected} không đạt</span>}
              </div>
              <div className="small muted" style={{ marginTop: 8 }}>
                Hạn NV {fmtDate(t.deadline)} · Hạn duyệt {fmtDate(t.reviewDeadline)}
              </div>
            </div>
          );
        })
      ) : (
        <div className="empty">Chưa có công việc nào trong tháng</div>
      )}

      <div className="card" style={{ background: 'var(--panel2)', marginTop: 8 }}>
        <h3>💰 Doanh thu{k.rev?.group ? ' (gộp cả nhóm)' : ''}</h3>
        <div className="grid kpi-grid">
          <div className="stat">
            <div className="label">Khoán</div>
            <div className="val sm">{k.rev ? fmtVND(k.rev.target) : '—'}</div>
          </div>
          <div className="stat">
            <div className="label">Thực tế</div>
            <div className="val sm">
              {k.rev ? fmtVND(k.rev.actual) : '—'}
              {(() => {
                const sup = (Number(k.rev?.supportCredit) || 0) + (Number(k.rev?.supportHandled) || 0);
                return sup > 0 ? <span style={{ color: '#16a34a', fontWeight: 600, fontSize: 12 }}> ({fmtVND(sup)})</span> : null;
              })()}
            </div>
          </div>
          <div
            className="stat"
            style={{ cursor: 'pointer', borderColor: 'var(--accent)' }}
            onClick={() => onOpenExpected(userId)}
            title="Bấm vào số để xem chi tiết theo trạng thái"
          >
            <div className="label">Dự kiến ▸</div>
            <div className="val sm" style={{ color: 'var(--accent)' }}>
              {exp ? fmtVND(exp.total) : '…'}
            </div>
            {exp && k.rev && k.rev.target > 0 && (
              <div
                className="small"
                style={{
                  marginTop: 4,
                  color: exp.expPct >= 80 ? 'var(--ok)' : exp.expPct >= 50 ? 'var(--warn)' : 'var(--danger)',
                }}
              >
                Dự kiến HT: {exp.expPct}%
              </div>
            )}
          </div>
          <div className="stat">
            <div className="label">% Đạt khoán</div>
            <div className="val" style={{ color: k.rev?.pct >= 80 ? 'var(--ok)' : k.rev?.pct >= 50 ? 'var(--warn)' : 'var(--danger)' }}>
              {k.rev ? k.rev.pct + '%' : '—'}
            </div>
          </div>
        </div>
      </div>

      <div className="card" style={{ background: 'var(--panel2)', marginTop: 8 }}>
        <h3>📉 Tỷ lệ cọc (theo tháng)</h3>
        <div className="row" style={{ marginBottom: 10 }}>
          <div className="stat" style={{ flex: 1 }}>
            <div className="label">Tỷ lệ cọc tháng này (Σ đã T.Toán / Σ tổng tiền)</div>
            <div className="val" style={{ color: k.debtRecover >= 80 ? 'var(--ok)' : k.debtRecover >= 50 ? 'var(--warn)' : 'var(--danger)' }}>
              {k.debtRecover}%
            </div>
          </div>
        </div>
        {customers.length ? (
          <table>
            <thead>
              <tr>
                <th>Mã KH</th>
                <th>Tên KH</th>
                <th className="r">Tổng tiền</th>
                <th className="r">Tỷ lệ cọc</th>
                <th className="r">Công nợ</th>
              </tr>
            </thead>
            <tbody>
              {customers.map((c) => (
                <tr key={c._id}>
                  <td>{c.code}</td>
                  <td>{c.name}</td>
                  <td className="r">{fmtVND(c.total)}</td>
                  <td className="r">
                    <RatioBadge r={c.ratio} />
                  </td>
                  <td className="r">{fmtVND(c.debt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="empty">Chưa phụ trách KH nào</div>
        )}
      </div>

      {/* Lịch sử KPI theo tháng */}
      <div className="card" style={{ background: 'var(--panel2)', marginTop: 8 }}>
        <h3>📅 Lịch sử KPI theo tháng</h3>
        {history.length ? (
          <table>
            <thead>
              <tr>
                <th>Tháng</th>
                <th className="r">DT thực tế</th>
                <th className="r">DT khoán</th>
                <th className="c">% Doanh thu</th>
                <th className="c">Tỷ lệ hoàn thành</th>
                <th className="c">Tổng việc</th>
                <th className="c">KH không đạt</th>
              </tr>
            </thead>
            <tbody>
              {history.map((h) => (
                <tr key={h.month}>
                  <td>
                    <b>{h.month}</b>
                  </td>
                  <td className="r">{fmtVND(h.actualRevenue)}</td>
                  <td className="r">{fmtVND(h.targetRevenue)}</td>
                  <td className="c">
                    <KpiPct p={h.revenuePct} />
                  </td>
                  <td className="c">
                    <KpiPct p={h.onTimeRate} />
                  </td>
                  <td className="c">{h.totalTasks}</td>
                  <td className="c">
                    {h.lateCount > 0 ? <span style={{ color: 'var(--danger)' }}>{h.lateCount}</span> : '0'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="empty">Chưa có lịch sử. Giám đốc bấm "Chốt KPI tháng này" để lưu lại.</div>
        )}
      </div>

      <div className="row" style={{ marginTop: 12 }}>
        <div className="spacer"></div>
        <button className="btn ghost" onClick={onClose}>
          Đóng
        </button>
      </div>
    </>
  );
}

function ExpectedModal({ userId, onBack, onClose }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    kpiApi.kpiExpected(userId, 'all').then(setData);
  }, [userId]);

  if (!data) return null;
  const { total, expRows, byStatus = [], expMonth, rev, expPct, statusLabel } = data;

  return (
    <>
      <span className="close" onClick={onClose}>
        ×
      </span>
      <h3>Chi tiết doanh thu dự kiến</h3>
      <div className="small muted" style={{ marginBottom: 12 }}>
        Đơn từ màn tổng đơn SocSon. Tính vào tháng {expMonth} nếu ngày dự kiến giao rơi trong tháng đó.
      </div>
      <div className="grid kpi-grid" style={{ marginBottom: 14 }}>
        <div className="stat">
          <div className="label">Tổng dự kiến tháng {expMonth}</div>
          <div className="val sm" style={{ color: 'var(--accent)' }}>
            {fmtVND(total)}
          </div>
        </div>
        {rev && (
          <>
            <div className="stat">
              <div className="label">Thực tế</div>
              <div className="val sm">{fmtVND(rev.actual)}</div>
            </div>
            <div className="stat">
              <div className="label">Khoán</div>
              <div className="val sm">{fmtVND(rev.target)}</div>
            </div>
            <div className="stat">
              <div className="label">% Dự kiến hoàn thành</div>
              <div className="val" style={{ color: expPct >= 80 ? 'var(--ok)' : expPct >= 50 ? 'var(--warn)' : 'var(--danger)' }}>
                {expPct}%
              </div>
            </div>
          </>
        )}
      </div>

      {/* Doanh thu dự kiến (về kịp tháng) gom theo từng trạng thái đơn */}
      <div style={{ fontSize: 13, fontWeight: 700, margin: '6px 0 8px' }}>Doanh thu dự kiến theo trạng thái đơn</div>
      {byStatus.length ? (
        <table style={{ marginBottom: 16 }}>
          <thead>
            <tr>
              <th>Trạng thái đơn</th>
              <th className="c">Số đơn</th>
              <th className="r">Doanh thu dự kiến</th>
            </tr>
          </thead>
          <tbody>
            {byStatus.map((s) => (
              <tr key={s.status}>
                <td>{s.status}</td>
                <td className="c">{s.count}</td>
                <td className="r">{fmtVND(s.total)}</td>
              </tr>
            ))}
            <tr>
              <td>
                <b>Tổng</b>
              </td>
              <td className="c">
                <b>{byStatus.reduce((a, b) => a + b.count, 0)}</b>
              </td>
              <td className="r">
                <b style={{ color: 'var(--accent)' }}>{fmtVND(total)}</b>
              </td>
            </tr>
          </tbody>
        </table>
      ) : (
        <div className="empty" style={{ marginBottom: 12 }}>Không có đơn dự kiến về kịp tháng này</div>
      )}

      <div style={{ fontSize: 13, fontWeight: 700, margin: '6px 0 8px' }}>Chi tiết từng đơn</div>
      {expRows.length ? (
        <table>
          <thead>
            <tr>
              <th>Mã đơn</th>
              <th>Sale</th>
              <th>Trạng thái</th>
              <th className="c">Tuyến</th>
              <th className="c">Ngày TT</th>
              <th className="c">Dự kiến giao</th>
              <th className="r">Giá trị</th>
              <th className="c">Tính?</th>
            </tr>
          </thead>
          <tbody>
            {expRows.map((r, idx) => (
              <tr key={idx} style={r.inMonth ? {} : { opacity: 0.5 }}>
                <td>{r.code}</td>
                <td className="small">{r.saleName}</td>
                <td className="small">{r.statusText || statusLabel[r.status] || r.status}</td>
                <td className="c">{r.route || '—'}</td>
                <td className="c small">{r.statusDate ? fmtDate(r.statusDate) : '—'}</td>
                <td className="c small">{r.eta ? fmtDate(r.eta) : '—'}</td>
                <td className="r">{fmtVND(r.value)}</td>
                <td className="c">
                  {r.inMonth ? <span className="chip done">✓ Tính</span> : <span className="chip todo">Sang tháng</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="empty">Không có đơn nào của nhân viên này</div>
      )}
      <div className="row" style={{ marginTop: 12 }}>
        <div className="spacer"></div>
        <button className="btn ghost" onClick={onBack}>
          ← Quay lại
        </button>
        <button className="btn" onClick={onClose}>
          Đóng
        </button>
      </div>
    </>
  );
}

// Bỏ mọi ký tự không phải chữ số (dấu phẩy, chấm, khoảng trắng) -> số nguyên.
function parseMoney(s) {
  return Number(String(s).replace(/[^\d]/g, '')) || 0;
}

// Lưới nhập KHOÁN cả năm: hàng = nhân viên, cột = 12 tháng.
function YearTargetGrid({ onClose, onSaved, toast }) {
  const nowYear = new Date().getFullYear();
  const [year, setYear] = useState(nowYear);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const years = [nowYear - 1, nowYear, nowYear + 1];

  useEffect(() => {
    setLoading(true);
    getYearTargets(year)
      .then((d) => setRows(d.rows.map((r) => ({ ...r, months: (r.months || []).slice(0, 12) }))))
      .finally(() => setLoading(false));
  }, [year]);

  function setCell(userId, mi, val) {
    setRows((rs) => rs.map((r) => {
      if (r.userId !== userId) return r;
      const months = r.months.slice();
      months[mi] = parseMoney(val);
      return { ...r, months };
    }));
  }
  function fillRow(userId) {
    setRows((rs) => rs.map((r) => {
      if (r.userId !== userId) return r;
      const v = r.months[0] || 0;
      return { ...r, months: new Array(12).fill(v) };
    }));
  }

  async function save() {
    setSaving(true);
    try {
      await saveYearTargets(year, rows.map((r) => ({ userId: r.userId, months: r.months })));
      toast('Đã lưu khoán cả năm');
      onSaved && onSaved();
      onClose();
    } catch (err) {
      toast(err.response?.data?.message || 'Có lỗi xảy ra', 'danger');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ width: '100%' }}>
      <span className="close" onClick={onClose}>×</span>
      <div className="row between" style={{ alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
        <h3 style={{ margin: 0 }}>Nhập khoán cả năm (theo tháng / nhân viên)</h3>
        <select value={year} onChange={(e) => setYear(Number(e.target.value))} style={{ width: 'auto' }}>
          {years.map((y) => <option key={y} value={y}>Năm {y}</option>)}
        </select>
      </div>
      {loading ? (
        <div className="empty">Đang tải…</div>
      ) : (
        <div className="tbl-wrap framed" style={{ maxHeight: '64vh', overflow: 'auto' }}>
          <table style={{ fontSize: 12.5 }}>
            <thead>
              <tr>
                <th style={{ position: 'sticky', left: 0, background: 'var(--panel)', zIndex: 2, minWidth: 150 }}>Nhân viên</th>
                {Array.from({ length: 12 }, (_, i) => <th key={i} style={{ textAlign: 'right', minWidth: 110 }}>T{i + 1}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.userId}>
                  <td style={{ position: 'sticky', left: 0, background: 'var(--panel)', zIndex: 1 }}>
                    <div style={{ fontWeight: 600 }}>{r.name}</div>
                    <div className="small muted" style={{ cursor: 'pointer' }} title="Điền T1 cho cả 12 tháng" onClick={() => fillRow(r.userId)}>
                      {r.group || roleName(r.role)} · ⤵ điền cả năm
                    </div>
                  </td>
                  {r.months.map((v, mi) => (
                    <td key={mi}>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={fmtNum(v || 0)}
                        onChange={(e) => setCell(r.userId, mi, e.target.value)}
                        style={{ textAlign: 'right', padding: '6px 8px' }}
                      />
                    </td>
                  ))}
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={13} className="empty">Không có nhân viên</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      <div className="row" style={{ marginTop: 14 }}>
        <div className="small muted">Khoán tháng hiện tại sẽ tự đồng bộ sang KPI & Báo cáo kinh doanh.</div>
        <div className="spacer"></div>
        <button className="btn ghost" onClick={onClose}>Hủy</button>
        <button className="btn" onClick={save} disabled={saving}>{saving ? 'Đang lưu…' : 'Lưu khoán'}</button>
      </div>
    </div>
  );
}

